import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { SessionGuard } from './core/guards/session/session.guard';
import { CheckoutGuard } from './core/guards/checkout/checkout.guard';
import { adminGuard } from './pages/private/admin/admin.guard';
import { checkinGuard } from './pages/private/eventos/checkin';

export const routes: Routes = [
  {
    // Consent central de cliente OAuth2 de terceiro. Fora do layout público (sem header) e com
    // sessão: a tela precisa saber quem é a pessoa para listar os eventos dela.
    path: 'conectar',
    title: 'Conectar aplicativo - Guedder',
    canActivate: [SessionGuard],
    loadComponent: () =>
      import('./pages/public/conectar/conectar.component').then((m) => m.ConectarComponent),
  },
  {
    path: 'oauth/consent',
    title: 'Autorizar integração - Guedder',
    loadComponent: () =>
      import('./pages/public/oauth-consent/oauth-consent.component').then(
        (m) => m.OAuthConsentComponent,
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('./layouts/public-layout/public-layout/public-layout.component').then(
        (m) => m.PublicLayoutComponent,
      ),
    children: [
      {
        path: '',
        title: 'Guedder - Eventos',
        loadComponent: () =>
          import('./pages/public/eventos-public/eventos-public.component').then(
            (m) => m.EventosPublicComponent,
          ),
      },
      {
        path: 'login',
        title: 'Entrar - Guedder',
        loadComponent: () =>
          import('./pages/public/login/login.component').then(
            (m) => m.LoginComponent,
          ),
      },
      // Retorno do Cognito no Authorization Code + PKCE. A URL precisa bater com
      // `guedder_web_callback_urls` no Terraform, senão dá redirect_mismatch em runtime.
      {
        path: 'callback',
        title: 'Entrando - Guedder',
        loadComponent: () =>
          import('./pages/public/callback/callback.component').then(
            (m) => m.CallbackComponent,
          ),
      },
      {
        path: 'evento/:id',
        title: 'Evento - Guedder',
        loadComponent: () =>
          import('./pages/public/evento-detail/evento-detail.component').then(
            (m) => m.EventoDetailComponent,
          ),
      },
      {
        path: 'evento/:id/lote/:lote',
        title: 'Evento - Guedder',
        loadComponent: () =>
          import('./pages/public/evento-detail/evento-detail.component').then(
            (m) => m.EventoDetailComponent,
          ),
      },
      {
        path: 'evento/:id/lote/:lote/form',
        title: 'Ingresso Expresso - Guedder',
        loadComponent: () =>
          import('./pages/public/express-ticket-form/express-ticket-form.component').then(
            (m) => m.ExpressTicketFormComponent,
          ),
      },
      {
        path: 'evento/:id/lote/:lote/checkin',
        title: 'Check-in - Guedder',
        loadComponent: () =>
          import('./pages/public/lote-checkin/lote-checkin.component').then(
            (m) => m.LoteCheckinComponent,
          ),
      },
      {
        path: 'perfil/:nomeVendedor',
        title: 'Perfil - Guedder',
        loadComponent: () =>
          import('./pages/private/minha-conta/minha-pagina/minha-pagina.component').then(
            (m) => m.MinhaPaginaComponent,
          ),
      },
      {
        path: 'private',
        canActivate: [SessionGuard],
        children: [
          {
            path: 'criar-evento',
            title: 'Criar Evento - Guedder',
            loadComponent: () =>
              import('./pages/private/eventos/criar-evento/criar-evento.component').then(
                (m) => m.CriarEventoComponent,
              ),
          },
          {
            path: 'criar-rapido',
            title: 'Criar Evento Rápido - Guedder',
            loadComponent: () =>
              import('./pages/private/eventos/criar-rapido/criar-rapido.component').then(
                (m) => m.CriarRapidoComponent,
              ),
          },
          {
            path: 'favoritos',
            title: 'Favoritos - Guedder',
            loadComponent: () =>
              import('./pages/private/eventos/favoritos/favoritos.component').then(
                (m) => m.FavoritosComponent,
              ),
          },
          {
            path: 'minha-conta',
            title: 'Minha Conta - Guedder',
            loadComponent: () =>
              import('./pages/private/minha-conta/minha-conta/minha-conta.component').then(
                (m) => m.MinhaContaComponent,
              ),
          },
          {
            path: 'minha-conta/excluir',
            title: 'Excluir Conta - Guedder',
            loadComponent: () =>
              import('./pages/private/minha-conta/excluir-conta/excluir-conta.component').then(
                (m) => m.ExcluirContaComponent,
              ),
          },
          {
            path: 'minha-conta/face-check',
            title: 'Verificação Facial - Guedder',
            loadComponent: () =>
              import('./pages/private/minha-conta/face-check/face-check.component').then(
                (m) => m.FaceCheckComponent,
              ),
          },
          {
            path: 'meus-eventos',
            title: 'Meus Eventos - Guedder',
            loadComponent: () =>
              import('./pages/private/eventos/eventos-mains/eventos-mains.component').then(
                (m) => m.EventosMainsComponent,
              ),
          },
          {
            path: 'meus-eventos/:idEvento',
            title: 'Editar Evento - Guedder',
            loadComponent: () =>
              import('./pages/private/eventos/eventos-insert-edit/eventos-insert-edit.component').then(
                (m) => m.EventosInsertEditComponent,
              ),
          },
          // Check-ins em rota própria. `checkinGuard` faz DUAS checagens
          // independentes: a feature flag `checkinsV2` (a tela está ligada?) e o
          // CASL `read Check-ins` (este usuário pode?). Flag desligada devolve a
          // aba antiga, que continua sendo o caminho padrão.
          {
            path: 'meus-eventos/:idEvento/checkin',
            title: 'Check-ins - Guedder',
            canActivate: [checkinGuard],
            loadComponent: () =>
              import('./pages/private/eventos/checkin').then(
                (m) => m.CheckinPageComponent,
              ),
          },
          {
            path: 'eventos/ingressos',
            title: 'Comprar Ingresso - Guedder',
            loadComponent: () =>
              import('./pages/private/ingresso/ingresso-comprar/ingresso-comprar.component').then(
                (m) => m.IngressoComprarComponent,
              ),
            canActivate: [CheckoutGuard],
          },
          {
            path: 'carteira',
            title: 'Carteira - Guedder',
            loadComponent: () =>
              import('./pages/private/carteira/carteira-main/carteira-main.component').then(
                (m) => m.CarteiraMainComponent,
              ),
          },
          {
            path: 'minhas-vendas',
            title: 'Minhas Vendas - Guedder',
            loadComponent: () =>
              import('./pages/private/carteira/minhas-vendas/minhas-vendas.component').then(
                (m) => m.MinhasVendasComponent,
              ),
          },
          {
            path: 'minhas-compras',
            title: 'Minhas Compras - Guedder',
            loadComponent: () =>
              import('./pages/private/minhas-compras/minhas-compras.component').then(
                (m) => m.MinhasComprasComponent,
              ),
          },
          {
            path: 'minha-pagina',
            title: 'Minha Página - Guedder',
            loadComponent: () =>
              import('./pages/private/minha-conta/minha-pagina/minha-pagina.component').then(
                (m) => m.MinhaPaginaComponent,
              ),
          },
          {
            path: 'evento/:idEvento/produtos',
            title: 'Produtos do Evento - Guedder',
            loadComponent: () =>
              import('./pages/private/eventos/produtos-evento/produtos-evento.component').then(
                (m) => m.ProdutosEventoComponent,
              ),
          },
          {
            path: 'admin',
            canActivate: [adminGuard],
            loadComponent: () =>
              import('./pages/private/admin/admin-shell/admin-shell.component').then(
                (m) => m.AdminShellComponent,
              ),
            children: [
              { path: '', redirectTo: 'eventos', pathMatch: 'full' },
              {
                path: 'eventos',
                title: 'Admin - Eventos - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/eventos/admin-eventos.component').then(
                    (m) => m.AdminEventosComponent,
                  ),
              },
              {
                path: 'produtores',
                title: 'Admin - Produtores - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/produtores/admin-produtores.component').then(
                    (m) => m.AdminProdutoresComponent,
                  ),
              },
              {
                path: 'funcionalidades',
                title: 'Admin - Funcionalidades - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/funcionalidades/admin-funcionalidades.component').then(
                    (m) => m.AdminFuncionalidadesComponent,
                  ),
              },
              {
                path: 'aviso-global',
                title: 'Admin - Aviso Global - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/aviso-global/admin-aviso-global.component').then(
                    (m) => m.AdminAvisoGlobalComponent,
                  ),
              },
              {
                path: 'comprador-bloqueado',
                title: 'Admin - Compradores Bloqueados - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/comprador-bloqueado/admin-comprador-bloqueado.component').then(
                    (m) => m.AdminCompradorBloqueadoComponent,
                  ),
              },
              {
                path: 'repasses',
                title: 'Admin - Repasses - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/repasses/admin-repasses.component').then(
                    (m) => m.AdminRepassesComponent,
                  ),
              },
              {
                path: 'operadora-vendas',
                title: 'Admin - Operadora de Vendas - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/operadora-vendas/admin-operadora-vendas.component').then(
                    (m) => m.AdminOperadoraVendasComponent,
                  ),
              },
              {
                path: 'taxacao',
                title: 'Admin - Taxação - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/taxacao/admin-taxacao.component').then(
                    (m) => m.AdminTaxacaoComponent,
                  ),
              },
              {
                path: 'auditoria-integracoes',
                title: 'Admin - Auditoria de Integrações - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/auditoria-integracoes/admin-auditoria-integracoes.component').then(
                    (m) => m.AdminAuditoriaIntegracoesComponent,
                  ),
              },
              {
                path: 'auditoria-alteracoes',
                title: 'Admin - Auditoria de Alterações - Guedder',
                loadComponent: () =>
                  import('./pages/private/admin/auditoria-alteracoes/admin-auditoria-alteracoes.component').then(
                    (m) => m.AdminAuditoriaAlteracoesComponent,
                  ),
              },
            ],
          },
          // { path: 'eventos/ingressos-dados/:id', component: IngressoDadosComponent },
          // { path: 'eventos/ingressos-venda', component: IngressoVendaComponent },
        ],
      },
    ],
  },
  // Cadastro facial por link (gerado em /api/v1/ingresso/{id}/link-facial). Só existe aqui, não no Flutter web.
  {
    path: 'accept-a-ticket',
    title: 'Cadastro facial - Guedder',
    loadComponent: () =>
      import('./pages/public/face-recognition-transfer/face-recognition-transfer.component').then(
        (m) => m.AcceptATicketComponent,
      ),
  },
  {
    path: 'face-recognition-transfer',
    redirectTo: 'accept-a-ticket',
    pathMatch: 'full',
  },
  {
    path: 'data-safety',
    title: 'Privacidade e Segurança - Guedder',
    loadComponent: () =>
      import('./pages/public/data-safety/data-safety.component').then(
        (m) => m.DataSafetyComponent,
      ),
  },
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
